/**
 * Drill-down for one slice of the Cost Explorer — a template, a person, a
 * channel or a cost driver.
 *
 * The ranking says how much; this answers the next three questions without a
 * request: what was it spent on, is it moving, and which of the *other*
 * dimensions does it break into (a template's spend by channel and person, a
 * person's by template and channel...). Everything is read off the explorer
 * model already in memory.
 *
 * Portalled to document.body: the page's entrance animations leave a transform
 * on ancestors, which would otherwise trap this `fixed` panel inside a card.
 */
import { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import { ArrowUpRight, X } from 'lucide-react'
import { useBodyScrollLock, usePrefersReducedMotion } from './BillingPrimitives'
import { formatCompact, formatInt, formatUsdAs } from '../../utils/billing'
import { DIMENSIONS, DRIVERS, SOURCES } from '../../utils/costExplorer'

const DRIVER_COLOR_INDEX = { documents: 0, normalisation: 3, email: 5, llm_fallback: 2 }
const SOURCE_LABEL = Object.fromEntries(SOURCES.map(s => [s.key, s.label]))

/** Does a request row belong to this slice, and how much of its cost does? */
function shareOf(row, dim, key) {
  if (dim === 'template') return row.templateKey === key ? row.cost : 0
  if (dim === 'person') return row.personKey === key ? row.cost : 0
  if (dim === 'source') return row.source === key ? row.cost : 0
  if (dim === 'driver') return row.drivers[key] || 0
  return 0
}

function groupBy(rows, valueOf, keyOf, labelOf) {
  const map = new Map()
  for (const row of rows) {
    const v = valueOf(row)
    if (v <= 0) continue
    const k = keyOf(row)
    const cur = map.get(k) || { key: k, label: labelOf(row), cost: 0 }
    cur.cost += v
    map.set(k, cur)
  }
  return [...map.values()].sort((a, b) => b.cost - a.cost)
}

function CrossCut({ title, rows, color, money }) {
  if (!rows.length) return null
  const total = rows.reduce((s, r) => s + r.cost, 0)
  const shown = rows.slice(0, 4)
  const rest = rows.slice(4).reduce((s, r) => s + r.cost, 0)
  return (
    <section>
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-500 mb-2">{title}</h3>
      <div className="space-y-2">
        {shown.map((r, i) => (
          <div key={r.key} className="animate-line-in" style={{ animationDelay: `${i * 40}ms` }}>
            <div className="flex items-baseline gap-3 text-[12.5px]">
              <span className="truncate text-gray-700 dark:text-gray-200">{r.label}</span>
              <span className="ml-auto tabular-nums font-semibold text-gray-900 dark:text-gray-100">{money(r.cost)}</span>
              <span className="w-10 text-right tabular-nums text-[11px] text-gray-400 dark:text-gray-500">
                {total > 0 ? `${Math.round((r.cost / total) * 100)}%` : ''}
              </span>
            </div>
            <div className="h-1.5 mt-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full animate-grow-x"
                style={{ width: `${total > 0 ? (r.cost / total) * 100 : 0}%`, background: color, animationDelay: `${i * 40 + 60}ms` }}
              />
            </div>
          </div>
        ))}
        {rest > 0 && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
            + {rows.length - 4} more · {money(rest)}
          </p>
        )}
      </div>
    </section>
  )
}

export default function CostBreakdownDrawer({ item, dim, model, color, chart, displayCurrency, periodLabel, onClose }) {
  const panelRef = useRef(null)
  const navigate = useNavigate()
  const reduced = usePrefersReducedMotion()
  const open = Boolean(item && model)
  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const previous = document.activeElement
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus?.()
    }
  }, [open, onClose])

  const money = v => formatUsdAs(v, displayCurrency)

  const detail = useMemo(() => {
    if (!open) return null
    const rows = model.requests.filter(r => shareOf(r, dim, item.key) > 0)
    const value = r => shareOf(r, dim, item.key)
    const cuts = []
    if (dim !== 'template') cuts.push({ title: 'By template', rows: groupBy(rows, value, r => r.templateKey, r => r.templateName) })
    if (dim !== 'source') cuts.push({ title: 'By how it arrived', rows: groupBy(rows, value, r => r.source, r => SOURCE_LABEL[r.source] || r.source) })
    if (dim !== 'person') cuts.push({ title: 'By person', rows: groupBy(rows, value, r => r.personKey, r => r.personName) })
    const top = [...rows].sort((a, b) => value(b) - value(a)).slice(0, 6).map(r => ({ ...r, slice: value(r) }))
    const trend = model.buckets.map(b => ({ label: b.label, fullLabel: b.fullLabel, cost: item.series[b.key] || 0 }))
    return { cuts: cuts.slice(0, 2), top, trend }
  }, [open, model, dim, item])

  if (!open) return null

  const dimMeta = DIMENSIONS.find(d => d.value === dim)
  const accent = color || chart.series[0]
  const driverParts = DRIVERS
    .map(d => ({ ...d, cost: item.drivers?.[d.key] || 0, color: chart.series[DRIVER_COLOR_INDEX[d.key]] }))
    .filter(d => d.cost > 0)
  const stats = [
    { label: periodLabel, value: money(item.cost) },
    { label: 'Of usage cost', value: `${(item.share * 100).toFixed(1)}%` },
    { label: 'Requests', value: formatInt(item.requests) },
    { label: 'Per request', value: item.perRequest !== null ? money(item.perRequest) : '—' },
    dim === 'driver' && item.key !== 'documents'
      ? { label: 'Processing runs', value: formatInt(item.jobs) }
      : { label: 'Pages', value: formatInt(item.pages) },
    dim === 'driver'
      ? { label: 'Per run', value: item.jobs > 0 ? money(item.cost / item.jobs) : '—' }
      : { label: 'AI tokens', value: formatCompact(item.tokens) },
  ]

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close details"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-gray-900/50 dark:bg-black/70 backdrop-blur-[2px] ${reduced ? '' : 'animate-backdrop-in'}`}
      />
      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`${item.label} cost details`}
        className={`fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[500px] bg-white dark:bg-gray-900
                    border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col focus:outline-none
                    ${reduced ? '' : 'animate-drawer-in'}`}
      >
        <header className="flex items-start gap-3 p-5 border-b border-gray-200 dark:border-gray-800">
          <span className="mt-1 w-3 h-3 rounded flex-shrink-0" style={{ background: accent }} />
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-500">
              {dimMeta?.label}
            </p>
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mt-0.5 break-words">{item.label}</h2>
            {(item.plain || item.email) && (
              <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">{item.plain || item.email}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <div className="grid grid-cols-3 gap-2.5">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 px-3 py-2.5 min-w-0 animate-card-in"
                style={{ animationDelay: `${80 + i * 35}ms` }}
              >
                <p className="text-[16px] font-bold text-gray-900 dark:text-gray-100 tabular-nums leading-tight truncate" title={s.value}>{s.value}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mt-1 truncate">{s.label}</p>
              </div>
            ))}
          </div>

          {dim !== 'driver' && driverParts.length > 0 && (
            <section>
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-500 mb-2">
                What it was spent on
              </h3>
              <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 gap-px">
                {driverParts.map(d => (
                  <span key={d.key} className="h-full animate-grow-x" style={{ width: `${(d.cost / item.cost) * 100}%`, background: d.color }} />
                ))}
              </div>
              <div className="mt-2.5 space-y-1.5">
                {driverParts.map(d => (
                  <div key={d.key} className="flex items-center gap-2 text-[12.5px]">
                    <i className="w-2 h-2 rounded-sm" style={{ background: d.color }} />
                    <span className="text-gray-700 dark:text-gray-200">{d.label}</span>
                    <span className="ml-auto tabular-nums font-semibold text-gray-900 dark:text-gray-100">{money(d.cost)}</span>
                    <span className="w-10 text-right tabular-nums text-[11px] text-gray-400 dark:text-gray-500">
                      {Math.round((d.cost / item.cost) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <h3 className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-500 mb-1">
              Over time · by {model.grouping}
            </h3>
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={detail.trend} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: chart.axis }} tickLine={false} axisLine={{ stroke: chart.grid }} minTickGap={18} />
                <Tooltip
                  cursor={{ fill: chart.grid, opacity: 0.45 }}
                  content={({ active, payload }) => (active && payload?.length ? (
                    <div className="rounded-lg px-2.5 py-1.5 text-xs shadow-lg"
                         style={{ background: chart.tooltipBg, border: `1px solid ${chart.tooltipBorder}`, color: chart.tooltipText }}>
                      <p className="font-semibold">{payload[0].payload.fullLabel}</p>
                      <p className="tabular-nums">{money(payload[0].value)}</p>
                    </div>
                  ) : null)}
                />
                <Bar dataKey="cost" fill={accent} radius={[3, 3, 0, 0]} maxBarSize={22} animationDuration={600} />
              </BarChart>
            </ResponsiveContainer>
          </section>

          {detail.cuts.map(cut => (
            <CrossCut key={cut.title} title={cut.title} rows={cut.rows} color={accent} money={money} />
          ))}

          {detail.top.length > 0 && (
            <section>
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-500 mb-2">
                Most expensive requests
              </h3>
              <div className="divide-y divide-gray-100 dark:divide-gray-800 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
                {detail.top.map(r => (
                  <button
                    key={r.id}
                    type="button"
                    disabled={!r.known}
                    onClick={() => { onClose(); navigate(`/requests/${r.id}`) }}
                    className="group w-full flex items-center gap-3 px-3 py-2.5 text-left
                               hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors disabled:cursor-default
                               focus:outline-none focus-visible:bg-navy-50 dark:focus-visible:bg-navy-900/30"
                  >
                    <span className="min-w-0">
                      <span className="block text-[12.5px] font-medium text-gray-900 dark:text-gray-100 truncate">{r.title}</span>
                      <span className="block text-[11px] text-gray-400 dark:text-gray-500 truncate">
                        #{r.id} · {r.templateName} · {formatInt(r.pages)} pages{r.runs > 1 ? ` · ${r.runs} runs` : ''}
                      </span>
                    </span>
                    <span className="ml-auto text-[12.5px] font-semibold tabular-nums text-gray-900 dark:text-gray-100">{money(r.slice)}</span>
                    {r.known && (
                      <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 group-hover:text-navy-600 dark:group-hover:text-navy-300 transition-colors" />
                    )}
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            Estimated AI processing charges at Microsoft&apos;s published rates, priced per processing run. Billed by
            Microsoft to the Azure subscription that runs this app, separate from your plan.
          </p>
        </footer>
      </aside>
    </>,
    document.body,
  )
}
